// Application data interface
export interface ApplicationData {
  id?: number;
  type: string;
  title: string;
  content?: string;
  icon?: string;
  link: string;
  ceo_name?: string;
  ceo_position?: string;
  is_visible: boolean;
  sort_order: number;
  created_at?: string;
  updated_at?: string;
}

// API response interfaces
interface ApplicationResponse {
  success: boolean;
  data: ApplicationData | null;
  message: string;
}

interface ApplicationsResponse {
  success: boolean;
  data: ApplicationData[];
  message: string;
}

interface ApplicationUpdateResponse {
  success: boolean;
  data: ApplicationData;
  message: string;
}

class ApplicationService {
  private baseUrl: string;

  constructor() {
    // With proxy configuration, we can use relative URLs
    this.baseUrl = process.env.REACT_APP_API_URL || '/api';
  }

  /**
   * Get all applications
   */
  async getApplications(): Promise<ApplicationsResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/applications`);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching applications:', error);
      return {
        success: false,
        data: [],
        message: 'Failed to fetch applications'
      };
    }
  }

  /**
   * Get single application by ID
   */
  async getApplication(id: number): Promise<ApplicationResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/applications/${id}`);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching application:', error);
      return {
        success: false,
        data: null,
        message: 'Failed to fetch application'
      };
    }
  }

  /**
   * Create new application
   */
  async createApplication(
    title: string,
    link: string,
    isVisible: boolean = true,
    sortOrder?: number,
    iconFile?: File
  ): Promise<ApplicationUpdateResponse> {
    try {
      const formData = new FormData();
      formData.append('title', title);
      formData.append('link', link);
      formData.append('is_visible', isVisible.toString());
      
      if (sortOrder !== undefined) {
        formData.append('sort_order', sortOrder.toString());
      }
      
      if (iconFile) {
        formData.append('icon', iconFile);
      }

      const response = await fetch(`${this.baseUrl}/applications`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error creating application:', error);
      return {
        success: false,
        message: 'Failed to create application',
        data: {} as ApplicationData
      };
    }
  }

  /**
   * Update existing application
   */
  async updateApplication(
    id: number,
    title?: string,
    link?: string,
    isVisible?: boolean,
    sortOrder?: number,
    iconFile?: File
  ): Promise<ApplicationUpdateResponse> {
    try {
      const formData = new FormData();
      
      if (title !== undefined) {
        formData.append('title', title);
      }
      
      if (link !== undefined) {
        formData.append('link', link);
      }
      
      if (isVisible !== undefined) {
        formData.append('is_visible', isVisible.toString());
      }
      
      if (sortOrder !== undefined) {
        formData.append('sort_order', sortOrder.toString());
      }
      
      if (iconFile) {
        formData.append('icon', iconFile);
      }

      const response = await fetch(`${this.baseUrl}/applications/${id}`, {
        method: 'PUT',
        body: formData
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating application:', error);
      return {
        success: false,
        message: 'Failed to update application',
        data: {} as ApplicationData
      };
    }
  }

  /**
   * Delete application
   */
  async deleteApplication(id: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/applications/${id}`, {
        method: 'DELETE'
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error deleting application:', error);
      return {
        success: false,
        message: 'Failed to delete application'
      };
    }
  }

  /**
   * Update sort order for multiple applications
   */
  async updateSortOrder(applications: { id: number; sort_order: number }[]): Promise<{ success: boolean; message: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/applications/sort-order/update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ applications })
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating sort order:', error);
      return {
        success: false,
        message: 'Failed to update sort order'
      };
    }
  }

  /**
   * Get image URL for application icon
   */
  getImageUrl(iconPath: string | null): string {
    if (!iconPath) {
      return '/assets/images/access-icon.png'; // Default icon
    }
    
    // If it's already a full URL, return as is
    if (iconPath.startsWith('http')) {
      return iconPath;
    }
    
    // If it's a relative path starting with /uploads, return as relative URL (proxy will handle it)
    if (iconPath.startsWith('/uploads')) {
      return iconPath; // Return relative path, proxy will handle routing to backend
    }
    
    // Otherwise assume it's an asset path
    return iconPath;
  }
}

// Export singleton instance
const applicationService = new ApplicationService();
export default applicationService;

// Export types for use in components
export type { ApplicationResponse, ApplicationsResponse, ApplicationUpdateResponse };
